// frontend/src/services/authService.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Module-level variable to deduplicate concurrent refresh calls
let refreshPromise = null;

export const signup = async (email, password, fullName) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, full_name: fullName }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Signup failed', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Signup failed');
    err.code = errorData.code || null;
    throw err;
  }

  return await response.json();
};

export const login = async (email, password) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Invalid credentials', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Invalid credentials');
    err.code = errorData.code || null;
    throw err;
  }

  return await response.json();
};

export const logout = async () => {
  await fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
};

export const refreshAccessToken = async () => {
  // Deduplicate: if a refresh is already in flight, return the same promise
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) return null;

      const data = await response.json();
      return data.access_token;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const fetchCurrentUser = async (accessToken) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch user', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch user');
    err.code = errorData.code || null;
    throw err;
  }

  return await response.json();
};

export const authenticatedFetch = async (url, options = {}, getToken, onRefresh) => {
  const token = getToken();
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const makeRequest = (accessToken) =>
    fetch(fullUrl, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
    });

  let response = await makeRequest(token);

  // On 401, try refreshing the token once and retry
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      onRefresh(newToken);
      response = await makeRequest(newToken);
    }
  }

  return response;
};
